import { Center, Icon, Spinner, Text, useColorModeValue, VStack } from '@chakra-ui/react'
import { Upload, XCircle } from 'lucide-react'
import React from 'react'

interface FileDropOverlayProps {
  label: string
  isUnsupportedFileType?: boolean
  unsupportedLabel?: string
  isUploading?: boolean
  uploadingLabel?: string
  hintLabel?: string
}

export const FileDropOverlay: React.FC<FileDropOverlayProps> = ({
  label,
  isUnsupportedFileType = false,
  unsupportedLabel,
  isUploading = false,
  uploadingLabel,
  hintLabel,
}) => {
  const overlayBg = useColorModeValue('whiteAlpha.800', 'blackAlpha.700')
  const panelBg = useColorModeValue('white', 'gray.800')
  const labelColor = useColorModeValue('gray.700', 'gray.100')
  const hintColor = useColorModeValue('gray.500', 'gray.400')

  const accentColor = isUnsupportedFileType ? 'red.500' : 'brand.500'
  const displayLabel = isUploading
    ? (uploadingLabel ?? label)
    : isUnsupportedFileType
      ? (unsupportedLabel ?? label)
      : label
  const icon = isUnsupportedFileType ? XCircle : Upload

  return (
    <Center
      position='fixed'
      inset={0}
      zIndex='overlay'
      bg={overlayBg}
      backdropFilter='blur(2px)'
      // Visual only: the drop is handled by the window-level listener, so the
      // overlay must not intercept drag events or it would fire its own
      // enter/leave pairs and make the depth counter flicker.
      pointerEvents='none'
    >
      <VStack
        spacing={4}
        px={12}
        py={10}
        bg={panelBg}
        borderRadius='2xl'
        borderWidth='2px'
        borderStyle='dashed'
        borderColor={accentColor}
        boxShadow='2xl'
      >
        {isUploading ? (
          <Spinner boxSize={10} color={accentColor} thickness='3px' speed='0.7s' />
        ) : (
          <Icon as={icon} boxSize={10} color={accentColor} />
        )}
        <Text fontSize='lg' fontWeight='medium' color={isUnsupportedFileType && !isUploading ? accentColor : labelColor}>
          {displayLabel}
        </Text>
        {hintLabel && !isUploading && !isUnsupportedFileType && (
          <Text fontSize='sm' color={hintColor}>
            {hintLabel}
          </Text>
        )}
      </VStack>
    </Center>
  )
}
